<template>
  <div class="Login-wrap ptb-100">
    <div class="container">
      <div class="row">
        <div class="col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-md-10 offset-md-1">
          <!-- App Download Section -->
          <div v-if="showAppDownload" class="login-form-wrap">
            <div class="login-header">
              <h3>Get Netwalletpay App</h3>
              <p>Register and manage your account on our mobile app</p>
            </div>
            <div class="login-form">
              <div class="login-body">
                <div class="app-download-section text-center">
                  <div class="app-icon-wrapper mb-4">
                    <i class="ri-smartphone-line app-icon"></i>
                  </div>
                  <h4 class="mb-3">Download Our Mobile App</h4>
                  <p class="mb-4 text-muted">
                    To create a new Netwalletpay account, please download our mobile app. 
                    Enjoy a seamless experience with easy registration, secure transactions, 
                    and full account management at your fingertips.
                  </p>
                  <div class="app-features mb-4">
                    <div class="feature-item">
                      <i class="ri-shield-check-line"></i>
                      <span>Secure Registration</span>
                    </div>
                    <div class="feature-item">
                      <i class="ri-exchange-line"></i>
                      <span>Easy Transactions</span>
                    </div>
                    <div class="feature-item">
                      <i class="ri-notification-line"></i>
                      <span>Instant Notifications</span>
                    </div>
                  </div>
                  <a href="https://play.google.com/store/apps/details?id=com.netwalletpay" 
                     target="_blank" 
                     class="btn style1 download-btn mb-3">
                    <i class="ri-google-play-fill me-2"></i>
                    Download from Google Play
                  </a>
                  <p class="mt-4 mb-0">
                    Already have an account?
                    <router-link class="link style1" to="/login">Sign In</router-link>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Original Registration Form (hidden but preserved for future use) -->
          <div v-else class="login-form-wrap">
            <div class="login-header">
              <h3>Register New Account</h3>
              <p>Open Netwalletpay account</p>
            </div>
            <div class="login-form">
              <div class="login-body">
                <div v-if="error">
                  <p class="text-danger">{{ error }}</p>
                </div>
                <form class="form-wrap" @submit.prevent="registerUser">
                  <div class="row">
                    <div class="col-lg-12" v-for="field in formFields" :key="field.name">
                      <div class="form-group">
                        <input :id="field.name" :name="field.name" :type="field.type" :placeholder="field.placeholder"
                          required v-model="formData[field.model]" />
                      </div>
                    </div>

                    <!-- Custom Dropdown -->
                    <div class="col-lg-12">
                      <div class="form-group">
                        <div class="custom-dropdown" @click="toggleDropdown">
                          <div class="selected-option">
                            <img v-if="selectedCountry" :src="selectedCountry.flags.svg" alt="flag" class="flag-icon" />
                            {{ selectedCountry ? selectedCountry.name.common : "Select Country" }}
                          </div>
                          <div v-if="dropdownOpen" class="custom-dropdown-menu">
                            <input type="text" v-model="searchQuery" class="dropdown-search"
                              placeholder="Search country..." />
                            <div v-for="c in filteredCountries" :key="c.code" class="custom-dropdown-item"
                              @click="e => selectCountry(e, c)">
                              <img :src="c.flags.svg" alt="flag" class="flag-icon" />
                              {{ c.name.common }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Hidden inputs -->
                    <input type="hidden" name="country" v-model="formData.country" />
                    <input type="hidden" name="currency" v-model="formData.currency" />

                    <div class="col-sm-12 col-12 mb-20">
                      <div class="checkbox style3">
                        <input type="checkbox" id="term-and-condition" name="term-and-condition" v-model="isAgreed" />
                        <label for="term-and-condition">
                          I Agree with the
                          <router-link class="link style1" to="/terms-of-service">
                            Terms &amp; Conditions
                          </router-link>
                        </label>
                      </div>
                    </div>
                    <div class="col-lg-12">
                      <div class="form-group">
                        <button class="btn style1" :disabled="isLoading">
                          <span v-if="isLoading">Loading...</span>
                          <span v-else>Register Now</span>
                        </button>
                      </div>
                    </div>
                    <div class="col-md-12">
                      <p class="mb-0">
                        Have an Account?
                        <router-link class="link style1" to="/login">Sign In</router-link>
                      </p>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { onMounted, onUnmounted, ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';

export default {
  name: 'Register',
  setup() {
    const router = useRouter();
    const isLoading = ref(false);
    const error = ref(null);
    const countries = ref([]);
    const dropdownOpen = ref(false);
    const selectedCountry = ref(null);
    const isAgreed = ref(false);
    const searchQuery = ref('');

    // Set to false to show the registration form instead
    const showAppDownload = ref(true);

    const formData = reactive({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      country: '',
      currency: ''
    });

    const formFields = [
      { name: 'fname', model: 'firstName', type: 'text', placeholder: 'First Name' },
      { name: 'lname', model: 'lastName', type: 'text', placeholder: 'Last Name' },
      { name: 'email', model: 'email', type: 'email', placeholder: 'Email' },
      { name: 'phone', model: 'mobile', type: 'text', placeholder: 'Phone number' }
    ];

    const fetchCountries = async () => {
      try {
        const response = await axios.get('/api/v1/lookup/countries');
        countries.value = response.data.data;
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };

    const selectCountry = (e, country) => {
      e.stopPropagation();
      dropdownOpen.value = false;
      selectedCountry.value = country;
      formData.country = country.code;
      formData.currency = country.currency.code;
    };

    const toggleDropdown = (event) => {
      event.stopPropagation();
      dropdownOpen.value = true;// !dropdownOpen.value;
    };

    const closeDropdownOnOutsideClick = (event) => {
      if (!event.target.closest('.dropdown-search')) {
        dropdownOpen.value = false;
      }
    };

    const registerUser = async () => {
      if (!isAgreed.value) {
        error.value = 'Please agree to the terms and conditions';
        return;
      }
      isLoading.value = true;
      try {
        await axios.post('/Account/Register', formData);
        error.value = null;
        router.push('/login');
      } catch (err) {
        console.error(err);
        error.value = err.response?.data?.message || 'Unexpected error, please try again later';
      } finally {
        isLoading.value = false;
      }
    };

    const filteredCountries = computed(() => {
      return countries.value.filter(c =>
        c.name.common.toLowerCase().includes(searchQuery.value.toLowerCase())
      );
    });

    onMounted(() => {
      fetchCountries();
      window.addEventListener('click', closeDropdownOnOutsideClick);
    });

    onUnmounted(() => {
      window.removeEventListener('click', closeDropdownOnOutsideClick);
    });

    return {
      isLoading,
      error,
      formData,
      formFields,
      countries,
      registerUser,
      selectedCountry,
      dropdownOpen,
      selectCountry,
      toggleDropdown,
      isAgreed,
      searchQuery,
      filteredCountries,
      showAppDownload
    };
  }
};
</script>

<style>
.custom-dropdown {
  position: relative;
  cursor: pointer;
  border-radius: 5px;
  background: #f9f9f9;
  border: none;
  width: 100%;
  height: 60px;
  padding: 10px 15px 10px 20px !important;
}

.selected-option {
  display: flex;
  align-items: center;
  height: 100%;
}

.flag-icon {
  width: 20px;
  height: 15px;
  margin-right: 8px;
}

.custom-dropdown-menu {
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border: none;
  background-color: #f9f9f9;
  z-index: 100;
  top: 60px;
  left: 0px;
}

.custom-dropdown-item {
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding-left: 20px;
}

.custom-dropdown-item:hover {
  background-color: #f0f0f0;
}

/* App Download Section Styles */
.app-download-section {
  padding: 20px 0;
}

.app-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 24px;
  margin-bottom: 20px;
}

.app-icon {
  font-size: 48px;
  color: white;
}

.app-features {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 30px 0;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #f8f9fa;
  border-radius: 50px;
  font-size: 14px;
  color: #495057;
}

.feature-item i {
  font-size: 18px;
  color: #6366f1;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 15px 40px;
  font-size: 16px;
}

.download-btn i {
  font-size: 20px;
}

.text-muted {
  color: #6c757d;
  line-height: 1.6;
}
</style>
